import React, { useEffect, useState } from "react";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import { isAllCoachFieldFilled, isAllUserFieldFilled } from "logic/logics";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { Box, Divider, IconButton, List, ListItem } from "@mui/joy"; // Divider 추가
import { ProgramContentType } from "data/BlockComponent";
import TaskProgressBar from "./progressbar/TaskProgressBar";
import {
  AddConceptNoteButton,
  DoneButton,
  TaskUndoneButton,
} from "components/task/change-status-button";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import { ProgramType } from "data/programData";
import { taskListAtom } from "recoil/taskListAtom";
import BackButton from "../../common/BackButton";
import useGoBack from "hooks/useGoBack";
import AddCommentButton from "../change-status-button/AddCommentButton";
import TaskRemoveButton from "../change-status-button/TaskRemoveButton";
import { theme } from "../../../styles/theme";
import TaskResetButton from "../change-status-button/TaskResetButton";
import CustomTypography from "../../common/CustomTypography";
import CustomModal from "../../common/CustomModal";

export default function TaskHeader({
  taskKey,
  title,
  emptyTitle,
  contentData,
  setContentData,
  setData,
  setMoveToIndex,
  thoughtRecordKey,
  activityRecordKey,
  meditationRecordKey,
  isDone,
  isInitialized,
  extraContent,
  lastDoneAt,
  onBack,
}: {
  taskKey: string;
  title?: string;
  emptyTitle?: boolean;
  contentData?: ProgramContentType[];
  setContentData?: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  setData?: React.Dispatch<React.SetStateAction<ProgramType | undefined>>;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  isDone?: boolean;
  isOpen?: boolean;
  isInitialized?: boolean;
  extraContent?: React.ReactNode;
  lastDoneAt?: string;
  onBack?: () => void;
}) {
  const hasData = !!contentData;

  const user = useRecoilValue(userAtom);
  const isCoach = user?.accessToken && user?.role !== "patient";
  const isHideDropdownMenu =
    taskKey === "progress" ||
    taskKey === "thoughtmap" ||
    taskKey === "valueCompass" ||
    taskKey === "chat";

  const isEarlyAccess = user?.isEarlyAccess || false;
  const isContextMenuEnabled =
    !isHideDropdownMenu && (thoughtRecordKey || meditationRecordKey);

  const isPatient = user?.role === "patient";
  const isDirty = isPatient && isDone === false;
  const isDoneButtonVisible = hasData && !isCoach;
  const shouldBlock = isDirty && isDoneButtonVisible;
  const [isGoBackEvent, setIsGoBackEvent] = useState(false);

  const isThoughtRecord = taskKey.includes("2-2-T") && thoughtRecordKey;
  const isAICommentAvailable =
    taskKey.includes("1-1-A") ||
    taskKey.includes("4-2-A") ||
    taskKey.includes("4-5-1-A") ||
    taskKey.includes("4-5-2-A") ||
    taskKey.includes("4-5-3-A") ||
    taskKey.includes("4-5-4-A") ||
    taskKey.includes("4-5-5-A") ||
    taskKey.includes("4-5-6-A");

  const handleConfirmNavigation = () => {
    setIsGoBackEvent(false);
    (onBack ?? goBack)();
  };

  const handleCancelNavigation = () => {
    setIsGoBackEvent(false);
  };

  const handleBack = () => {
    if (shouldBlock) {
      setIsGoBackEvent(true);
    } else {
      (onBack ?? goBack)();
    }
  };

  const taskTitle = useGetTaskTitleFromTaskKey(taskKey);

  const taskListState = useRecoilValue(taskListAtom);
  const isOpen = taskListState?.find(
    (element) => element.taskKey === taskKey
  )?.isOpen;

  const goBack = useGoBack();

  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const handlePointerDown = () => {
      setShowMenu(false);
    };
    window.addEventListener("pointerdown", handlePointerDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  // 코치용 메뉴 아이템을 동적으로 생성
  const coachDropdownMenuItems = [];
  const patientDropdownMenuItems = [];

  if (isCoach) {
    if (setContentData) {
      coachDropdownMenuItems.push(
        <ListItem key="undone" sx={{ p: 0 }}>
          <TaskUndoneButton
            taskKey={taskKey}
            setData={setData}
            contentData={contentData}
            setContentData={setContentData}
            thoughtRecordKey={thoughtRecordKey}
            activityRecordKey={activityRecordKey}
            meditationRecordKey={meditationRecordKey}
            isDone={isDone}
            onClose={() => setShowMenu(false)}
          />
        </ListItem>
      );
      coachDropdownMenuItems.push(
        <ListItem key="reset" sx={{ p: 0 }}>
          <TaskResetButton
            taskKey={taskKey}
            setData={setData}
            contentData={contentData}
            setContentData={setContentData}
            thoughtRecordKey={thoughtRecordKey}
            activityRecordKey={activityRecordKey}
            meditationRecordKey={meditationRecordKey}
            isDone={isDone}
            isInitialized={isInitialized}
            onClose={() => setShowMenu(false)}
          />
        </ListItem>
      );
    }
    if (taskKey) {
      coachDropdownMenuItems.push(
        <ListItem key={taskKey} sx={{ p: 0 }}>
          <TaskRemoveButton
            taskKey={taskKey}
            thoughtRecordKey={thoughtRecordKey}
            meditationRecordKey={meditationRecordKey}
            onClose={() => setShowMenu(false)}
          />
        </ListItem>
      );
    }
  } else {
    patientDropdownMenuItems.push(
      <ListItem key={taskKey} sx={{ p: 0 }}>
        <TaskRemoveButton
          taskKey={taskKey}
          thoughtRecordKey={thoughtRecordKey}
          meditationRecordKey={meditationRecordKey}
          onClose={() => setShowMenu(false)}
        />
      </ListItem>
    );
  }

  return (
    <>
      <Stack>
        <Stack
          direction={"column"}
          sx={{
            px: 0,
            height: "var(--StickyHeader-height)",
          }}
        >
          <Stack
            direction={"row"}
            justifyContent="flex-end"
            alignItems="center"
            spacing={"8px"}
            sx={{
              flex: 1,
              height: "100%",
              pl: "8px",
              pr: isContextMenuEnabled ? "8px" : "20px",
            }}
          >
            <BackButton onBack={handleBack} />
            <Stack
              direction="row"
              alignItems={"center"}
              spacing={1}
              flexGrow={1}
            >
              {isCoach &&
                contentData &&
                !isAllCoachFieldFilled(contentData) && (
                  <Box
                    sx={{
                      color: "danger.500",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <span className="material-symbols-outlined ">error</span>
                  </Box>
                )}
              <Stack direction="row" alignItems={"center"} spacing={2}>
                <CustomTypography
                  variant={"Body"}
                  flexGrow={1}
                  sx={{
                    fontWeight: 700,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {!emptyTitle ? title || taskTitle : ""}
                </CustomTypography>
              </Stack>
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems={"center"}
              spacing={1}
              sx={{ py: 0 }}
            >
              {!isCoach ? (
                <>
                  {hasData &&
                    thoughtRecordKey &&
                    lastDoneAt &&
                    !isDone &&
                    isAllUserFieldFilled(contentData, false) && (
                      <Typography
                        variant={"Footnote"}
                        sx={{
                          whiteSpace: "pre-wrap",
                          color: theme.vars.palette.Content.HighlightWithAlert,
                        }}
                      >
                        마치기를 눌러주세요
                      </Typography>
                    )}
                  {hasData && (
                    <DoneButton
                      taskKey={taskKey}
                      data={contentData}
                      thoughtRecordKey={thoughtRecordKey}
                      meditationRecordKey={meditationRecordKey}
                      isDone={isDone}
                      isOpen={isOpen}
                      size="sm"
                    />
                  )}
                  {isContextMenuEnabled && (
                    <Box sx={{ position: "relative" }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu((prev) => !prev);
                        }}
                        sx={{
                          width: "36px",
                          height: "36px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            fontSize: "24px",
                          }}
                        >
                          more_vert
                        </span>
                      </IconButton>
                      {showMenu && patientDropdownMenuItems.length > 0 && (
                        <Box
                          className="absolute z-[30] right-0 top-[110%]"
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <List
                            orientation="vertical"
                            sx={{
                              width: "max-content",
                              borderWidth: 1,
                              borderColor: theme.vars.palette.Border.SuperWeak,
                              backgroundColor:
                                theme.vars.palette.Surface.Forward,
                              borderRadius: "12px",
                              boxShadow:
                                "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
                              overflow: "hidden",
                              p: 0,
                              "--List-gap": 0,
                              "--ListItem-paddingY": 0,
                            }}
                          >
                            {patientDropdownMenuItems.map((item, index) => (
                              <React.Fragment key={item.key}>
                                {item}
                                {index <
                                  patientDropdownMenuItems.length - 1 && (
                                  <Divider />
                                )}
                              </React.Fragment>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {user?.userId !== 52980 &&
                    user?.userId !== 61875 &&
                    user?.userId !== 909 &&
                    /*TODO 송민경, 육동연 선생님 질문하기 제거, 내담자 수에 맞게 단계적 적용 필요*/ (isThoughtRecord ||
                      isAICommentAvailable) && (
                      <AddCommentButton
                        key={`${taskKey || "noTaskKey"}_${thoughtRecordKey || "noThoughtRecordKey"}`}
                        taskKey={taskKey}
                        thoughtRecordKey={thoughtRecordKey}
                        contentData={contentData}
                        setContentData={setContentData}
                        setMoveToIndex={setMoveToIndex}
                      />
                    )}
                  {isThoughtRecord && (
                    <AddConceptNoteButton
                      thoughtRecordKey={thoughtRecordKey!!}
                    />
                  )}
                  {!isHideDropdownMenu && (
                    <Box sx={{ position: "relative" }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu((prev) => !prev);
                        }}
                        sx={{
                          width: "36px",
                          height: "36px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            fontSize: "24px",
                          }}
                        >
                          more_vert
                        </span>
                      </IconButton>
                      {showMenu && coachDropdownMenuItems.length > 0 && (
                        <Box
                          className="absolute z-[30] right-0 top-[110%]"
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <List
                            orientation="vertical"
                            sx={{
                              width: "max-content",
                              borderRadius: "12px",
                              boxShadow:
                                "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
                              overflow: "hidden",
                              borderWidth: 1,
                              borderColor: theme.vars.palette.Border.SuperWeak,
                              backgroundColor:
                                theme.vars.palette.Surface.Forward,
                              px: 0,
                              py: 0,
                              "--List-gap": 0,
                              "--ListItem-paddingY": 0,
                            }}
                          >
                            {coachDropdownMenuItems.map((item, index) => (
                              <React.Fragment key={item.key}>
                                {item}
                                {index < coachDropdownMenuItems.length - 1 && (
                                  <Divider
                                    color={theme.vars.palette.Border.SuperWeak}
                                  />
                                )}
                              </React.Fragment>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
        {extraContent}
      </Stack>
      {setMoveToIndex && (
        <TaskProgressBar
          key={`${thoughtRecordKey || meditationRecordKey || taskKey}`}
          taskKey={taskKey}
          data={contentData}
          setMoveToIndex={setMoveToIndex}
          lastDoneAt={lastDoneAt}
        />
      )}
      <CustomModal
        open={shouldBlock && isGoBackEvent}
        onClose={handleCancelNavigation}
        title={"활동지를 마치지 않았어요"}
        subtitle={"마무리 짓지 않고 여기서 그만할까요?"}
        actions={[
          {
            label: "마치지 않고 나가기",
            onClick: handleConfirmNavigation,
            variant: "Soft",
            color: "danger",
          },
          {
            label: "마저 계속하기",
            onClick: handleCancelNavigation,
            variant: "Plain",
          },
        ]}
      />
    </>
  );
}
